<template>
  <e-combobox
    v-model="boxValue"
    v-model:search="value"
    ref="comboboxRef"
    :maska="activeCountry.mask"
    :placeholder="activeCountry.mask"
    :items="countriesArr"
    :rules="rules"
    :menu="isMenuOpened"
    :menu-props="{
      openOnFocus: false,
      openOnClick: false,
    }"
    active
    persistent-placeholder
    :label="label || 'Телефон'"
    item-title="name"
    @on-item-click="onItemClick"
    @update:menu="onMenuUpdate"
    @update:input-value="onInputUpdate"
  >
    <template #prepend-inner>
      <div class="phone-field__country" @click="toggleMenu">
        <img :src="activeCountry.flag" class="phone-field__country-flag" />
        <e-icon
          name="arrowDropdown"
          size="8"
          class="phone-field__dropdown"
          :class="{ 'phone-field__dropdown-rotate': isMenuOpened }"
        />
      </div>
    </template>
    <template #item="{ item }">
      <div class="d-flex align-center input-text">
        <img class="mr-2 phone-field__country-flag" :src="item.raw.flag" />
        {{ item.raw.name }}
      </div>
    </template>
  </e-combobox>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { computed, nextTick, ref, toRef } from 'vue';
import type { EComboboxModelType, InputRules } from '@/core/types/components/components';
import { ECombobox, EIcon } from '@/core/components/ui';
import { flags } from '@/core/components/ui/EIcon/flags';
import { countries, type Country } from '@/core/consts/locale';
import { phoneMasks } from '@/core/consts/masks';
import { clearPhoneFormat } from '@/core/utils/string';
import { phoneRule } from '@/core/utils/validation';

const props = defineProps<{
  modelValue: string | null;
  label?: string;
  rules?: InputRules;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: string | null): void;
}>();

const boxValue = ref();

const activeCountry = ref<Country>({
  name: 'Россия, +7',
  flag: flags.RU,
  mask: phoneMasks.RU,
});

const value = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value ? clearPhoneFormat(value) : value);
  },
});

boxValue.value = props.modelValue;

if (props.modelValue) {
  const countryIndex = Object.values(phoneMasks).findIndex((mask) =>
    props.modelValue!.startsWith(mask.replace(/\D/g, '')),
  );

  if (countryIndex) {
    const countryCode = Object.keys(phoneMasks)[countryIndex];

    if (countries[countryCode]) {
      activeCountry.value = countries[countryCode];
    }
  }
}

const rules = toRef(() => {
  const length = activeCountry.value.mask.replace(/[^0-9#]/g, '').length;

  let arr = [phoneRule(length)];

  if (props.rules) {
    arr = [...arr, ...props.rules];
  }

  return arr;
});

const isMenuOpened = ref(false);

const comboboxRef = ref();

const toggleMenu = () => {
  isMenuOpened.value = !isMenuOpened.value;
};

const closeMenu = () => {
  isMenuOpened.value = false;
};

const onMenuUpdate = (value: boolean) => {
  if (!value) {
    isMenuOpened.value = value;
  }
};

let disabled = false;

const onInputUpdate = useDebounceFn(() => {
  if (disabled) {
    return;
  }

  isMenuOpened.value = true;
  nextTick(() => {
    isMenuOpened.value = false;
  });
}, 500);

const onItemClick = (item: EComboboxModelType<Country>) => {
  disabled = true;

  if (item && typeof item !== 'string') {
    activeCountry.value = item;
  }

  nextTick(() => {
    value.value = null;
    boxValue.value = activeCountry.value.mask;
  });

  setTimeout(() => {
    disabled = false;
  }, 600);

  closeMenu();
};

const countriesArr = computed(() => Object.values(countries));
</script>

<style scoped lang="scss">
.phone-field {
  &__country {
    position: relative;
    z-index: 999;

    display: flex;
    align-items: center;

    margin-right: -14px;
    margin-left: -3px;
    padding: 8px;

    color: $text-secondary-color;

    border-radius: $border-radius;

    cursor: pointer;

    &-flag {
      display: block;

      width: 20px;

      border: 1px solid $border-color-light;
      border-radius: 2px;
    }

    @include hover {
      background-color: $primary-light-color;
    }
  }

  &__dropdown {
    margin-left: 8px;

    transition: transform $transition-duration;

    &-rotate {
      transform: rotate(180deg);
    }
  }
}
</style>
